import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FilterField,
  FilterPanel,
  KPI,
  KPIGrid,
  InlineLoading,
  LoadingState,
  Page,
  Select,
  Table,
  type Column,
  Text,
} from "@marekl/adminer/components";
import { useTableSort, useUrlFilters } from "@marekl/adminer/hooks";
import { formatDateTime } from "@marekl/adminer/format";
import { Download } from "lucide-react";
import { AppTopbar } from "../../../shell/AppTopbar";
import { competitionCrumbs, useCompetitionReadOnly } from "../../../shell/CompetitionLayout";
import { isFrozen } from "../../../lib/competitionState";
import { useCompetition } from "../../../lib/queries/competitions";
import { useRuleSetDocument } from "../../../lib/queries/rules";
import { useCompetitionEntries, type Entry } from "../../../lib/queries/entries";
import { startListBlocks, useStartList } from "../../../lib/queries/startlists";
import { competitionMusicExportUrl, useMusicExportStatus } from "../../../lib/queries/music";
import { comboLabel, indexDocument } from "../../../lib/rulesDocument";
import { formatDuration, musicBadge, musicWarningItems } from "../../../lib/musicState";
import { EntryDetailModal } from "../../../components/EntryDetailModal";
import { BadgeRow } from "../../../components/BadgeRow";
import styles from "./CompetitionMusicPage.module.css";
import type { ID } from "../../../lib/id";

type Filter = "all" | "missing" | "warnings";

const FILTER_LABELS: Record<Filter, string> = {
  all: "Vše",
  missing: "Jen chybějící",
  warnings: "Jen s připomínkami",
};

function isFilter(value: string): value is Filter {
  return value in FILTER_LABELS;
}

// The organizer's Hudba page: what will play, what is missing, and the
// archive the sound desk works from.
//
// Uploading here has no deadline — after the uzávěrka hudby, fixing a
// club's music is the organizer's job — and the warnings are written for
// them, so the grace band is named (see warningMessage).
export function CompetitionMusicPage() {
  const { id } = useParams();
  // A competitions_viewer reads this page; nothing on it is offered.
  const readOnly = useCompetitionReadOnly();
  const compId = id ?? "";

  const { data: competition, isLoading, error, refetch } = useCompetition(compId);
  const { data: document } = useRuleSetDocument(competition?.rule_set_id);
  const { data: entries = [], isLoading: entriesLoading } = useCompetitionEntries(compId);
  // Whether the archive can be built is the SERVER's answer, from the
  // same walk that builds it. This page used to decide for itself and
  // counted performances the export skips — a scratched row owes nothing
  // — so the banner stayed up and the button stayed dead over a ZIP the
  // server would have produced happily.
  const { data: exportStatus } = useMusicExportStatus(compId);
  // In the URL, so an organizer can send „tyhle tři nemají hudbu" as a link
  // rather than as an instruction to click things.
  const [searchParams, setSearchParams] = useSearchParams();
  const { setFilter } = useUrlFilters({
    searchParams,
    setSearchParams,
    paginationKey: "page",
  });
  const filterParam = searchParams.get("filter") ?? "";
  const filter: Filter = isFilter(filterParam) ? filterParam : "all";
  // An id rather than the row: uploading refetches the entry list, and a
  // snapshot taken when the dialog opened would keep showing „Chybí
  // hudba" over a file that is already stored.
  const [openId, setOpenId] = useState<ID | null>(null);

  const index = useMemo(() => (document ? indexDocument(document) : null), [document]);

  // Who is scratched. Scratching lives on the START LIST row, not on the
  // entry — EntryStatus is only submitted/withdrawn — so an entry filter
  // alone cannot see it.
  //
  // This is the same rule the export walk applies, and the counts have to
  // use it too: the banner already came from the server, so a page that
  // kept counting scratched rows as missing contradicted its own banner
  // and sent the organizer chasing a club that owes nothing.
  const { data: startList } = useStartList(compId);
  const scratched = useMemo(() => {
    const out = new Set<ID>();
    for (const item of startListBlocks(startList)) {
      for (const row of item.entries ?? []) {
        if (row.scratched) out.add(row.competition_entry_id);
      }
    }
    return out;
  }, [startList]);

  // Only entries that will actually perform. An odhláška owes nothing, and
  // neither does somebody struck off the running order.
  const performing = useMemo(
    () => entries.filter((e) => e.status === "submitted" && !scratched.has(e.id)),
    [entries, scratched],
  );

  const counts = useMemo(() => {
    let uploaded = 0;
    let missing = 0;
    // The two sources the club owes nothing for, in ONE number.
    //
    // They were two tiles, and the question a tile answers is „kolik
    // hudby musím řešit" — for which the difference between „hraje se
    // oficiální nahrávka" and „pustí se něco, co nikdo předem nezná" is
    // no difference at all: both are starts nobody is waiting on a club
    // for. The distinction still matters start by start, so the LIST
    // keeps it — see musicBadge's organizer badge below.
    let federationOrDesk = 0;
    let warnings = 0;
    for (const e of performing) {
      if (e.music_source === "federation" || e.music_source === "none") federationOrDesk++;
      else if (e.music) uploaded++;
      else missing++;
      if (e.music && e.music.warnings.length > 0) warnings++;
    }
    return { uploaded, missing, federationOrDesk, warnings };
  }, [performing]);

  const rows = useMemo(() => {
    switch (filter) {
      case "missing":
        return performing.filter((e) => e.music_source === "own" && !e.music);
      case "warnings":
        return performing.filter((e) => (e.music?.warnings.length ?? 0) > 0);
      default:
        return performing;
    }
  }, [performing, filter]);

  const crumbs = competitionCrumbs(compId, competition, { label: "Hudba" });

  // Hoisted for useTableSort, which re-sorts on the columns identity.
  const columns = useMemo<Column<Entry>[]>(
    () => [
            {
              key: "cp",
              header: "ČP",
              numeric: true,
              sortable: true,
              sortValue: (e) => e.entry_number,
              render: (e) => e.entry_number,
            },
            {
              key: "team",
              header: "Tým",
              sortable: true,
              sortValue: (e) => e.team_name ?? null,
              render: (e) => e.team_name ?? "—",
            },
            {
              key: "combo",
              header: "Kombinace",
              sortable: true,
              sortValue: (e) => {
                const ref = index?.combos.get(e.discipline_category_id);
                return ref ? comboLabel(ref) : null;
              },
              render: (e) => {
                const ref = index?.combos.get(e.discipline_category_id);
                return ref ? comboLabel(ref) : "—";
              },
            },
            {
              key: "length",
              header: "Délka",
              sortable: true,
              // Milliseconds, not the formatted "2:45" — that string sorts by
              // its first digit. No music is missing, so it sorts last.
              sortValue: (e) => e.music?.duration_ms ?? null,
              render: (e) =>
                e.music ? (
                  formatDuration(e.music.duration_ms)
                ) : (
                  <Text variant="muted">—</Text>
                ),
            },
            {
              key: "state",
              header: "Stav",
              sortable: true,
              // The badge's own words, so "Chybí hudba" groups together —
              // the click an organizer chasing missing tracks wants.
              //
              // „organizer" on both, and they must agree: this is the one
              // list where improvizace reads „Zajišťuje zvukař" instead of
              // „Hudba ČNSMS", so a sort computed from the club's wording
              // would file it under a label the row does not show.
              sortValue: (e) => musicBadge(e.music_source, e.music !== null, "organizer").label,
              render: (e) => {
                const badge = musicBadge(e.music_source, e.music !== null, "organizer");
                return (
                  <BadgeRow>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    {e.music &&
                      musicWarningItems(e.music.warnings, "organizer").map((w) => (
                        <Badge key={w.kind} variant={w.variant}>
                          {w.label}
                        </Badge>
                      ))}
                  </BadgeRow>
                );
              },
            },
            {
              key: "actions",
              header: "",
              // Offered on every row, federation and improvizace
              // included: the dialog answers „what plays here?", which
              // is a question those rows have an answer to. The upload
              // lives inside it for the ones that owe a file.
              render: (e) => (
                <Table.Actions>
                  <Table.Action appearance="button" onClick={() => setOpenId(e.id)}>Detail</Table.Action>
                </Table.Actions>
              ),
            },
    ],
    [index],
  );

  const tableSort = useTableSort(rows, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "cp", dir: "asc" },
  });

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (error || !competition) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState title="Soutěž se nepodařilo načíst" error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  const openEntry = openId === null ? null : (entries.find((e) => e.id === openId) ?? null);

  const missingOnList = exportStatus?.missing ?? [];
  const exportBlocked = !exportStatus?.ready;
  // Unknown is not the same as blocked: while the query is in flight the
  // button waits, but there is nothing to warn about yet.
  const exportReason = !exportStatus
    ? null
    : !exportStatus.published
      ? "Startovní listina není zveřejněná. Hudba se stahuje v pořadí podle ní."
      : missingOnList.length > 0
        ? `Chybí hudba u ${missingOnList.length} vystoupení. Doplňte ji, nebo je přeškrtněte ve startovní listině.`
        : "Startovní listina zatím neobsahuje žádné vystoupení.";

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title="Hudba"
          subtitle={competition.name}
          actions={
            exportBlocked ? (
              <Button variant="primary" disabled startIcon={<Download size={16} />}>
                Stáhnout ZIP pro zvukaře
              </Button>
            ) : (
              // A plain anchor: the endpoint takes the session cookie, so
              // the browser's own download does the job without a blob
              // round trip through JavaScript.
              <a
                className={styles.exportLink}
                href={competitionMusicExportUrl(compId)}
                download
              >
                <Button variant="primary" startIcon={<Download size={16} />}>
                  Stáhnout ZIP pro zvukaře
                </Button>
              </a>
            )
          }
        />
        <Page.Content stack gap="lg">
          {exportBlocked && exportReason && (
            <Alert variant="warn">
              {exportReason}
              {missingOnList.length > 0 && (
                // Naming them, because „chybí u 3 vystoupení" leaves the
                // organizer with nobody to ring.
                <ul className={styles.missingList}>
                  {missingOnList.map((m) => (
                    <li key={`${m.start_number}-${m.title}`}>
                      {m.start_number}. {m.title} · {m.team_name}
                      {m.federation && " (chybí oficiální nahrávka ČNSMS)"}
                    </li>
                  ))}
                </ul>
              )}
            </Alert>
          )}

          {/* A named region: the tiles and the badges below share their
              wording, which is right for a reader and ambiguous for
              anything addressing them. */}
          <section aria-label="Přehled hudby">
            {/* The tiles are derived from `entries`, so before it lands they
                read 0 / 0 / 0 / 0: four confident numbers saying nothing is
                owed, above a table that is visibly still loading. */}
            {entriesLoading ? (
              <InlineLoading>Načítám přehled hudby…</InlineLoading>
            ) : (
              <KPIGrid>
                <KPI label="Nahráno" value={counts.uploaded} />
                {/* „Chybí", not „Chybí hudba": the badge in the table below
                    already says the full phrase, and a KPI is a count. */}
                <KPI label="Chybí" value={counts.missing} hi={counts.missing > 0} />
                <KPI label="S připomínkami" value={counts.warnings} />
                {/* „Hudba ČNSMS" covers improvizaci too: the tile counts
                    starts no club owes a file for, and the club is told the
                    same phrase for both. */}
                <KPI label="Hudba ČNSMS" value={counts.federationOrDesk} />
              </KPIGrid>
            )}
          </section>

          <FilterPanel>
            <FilterField>
              <Select
                label="Zobrazit"
                value={filter}
                onChange={(e) => setFilter("filter", e.target.value === "all" ? null : e.target.value)}
              >
                {(Object.keys(FILTER_LABELS) as Filter[]).map((f) => (
                  <option key={f} value={f}>
                    {FILTER_LABELS[f]}
                  </option>
                ))}
              </Select>
            </FilterField>
          </FilterPanel>

          <Table<Entry>
            framed
            {...tableSort.tableProps}
            rowKey={(e) => e.id}
            isLoading={entriesLoading}
            columns={columns}
            empty={
              <EmptyState
                title={filter === "all" ? "Zatím žádné přihlášky" : "Nic k zobrazení"}
                description={
                  filter === "missing"
                    ? "Všechna vystoupení mají hudbu."
                    : filter === "warnings"
                      ? "Žádná nahrávka nemá připomínky."
                      : "Na soutěž se zatím nikdo nepřihlásil."
                }
              />
            }
          />

          <Text variant="muted">
            Uzávěrka hudby{" "}
            {competition.effective_music_deadline
              ? formatDateTime(competition.effective_music_deadline)
              : "není nastavena"}
          </Text>
        </Page.Content>
      </Page>

      {openId !== null && (
        <EntryDetailModal
          entry={openEntry}
          combo={openEntry ? index?.combos.get(openEntry.discipline_category_id) : undefined}
          // No deadline here: after the uzávěrka hudby, fixing a club's
          // recording is the organizer's job. Until Dokončeno: then the
          // competition is frozen whole.
          canUploadMusic={!isFrozen(competition) && !readOnly}
          audience="organizer"
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}
